import type { AxiosError } from 'axios';

/**
 * Estructura de error del backend (normalizada para todos los casos, incluyendo 422).
 *
 * Backend siempre devuelve esta estructura. Para errores de validación (422),
 * `details.errors` contiene la lista de errores por campo.
 */
export interface ApiErrorBody {
  detail: string;
  error_code: string;
  details?: ApiErrorDetails;
}

export interface ApiErrorDetails {
  /** Para errores 422 de validación. */
  errors?: ApiValidationError[];
  /** Otros campos custom del backend. */
  [key: string]: unknown;
}

export interface ApiValidationError {
  field: string;
  message: string;
  type: string;
}

/**
 * Wrapper de error tipado del cliente HTTP.
 * Encapsula la respuesta del backend y el status HTTP.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly details: ApiErrorDetails;
  readonly originalError?: AxiosError<ApiErrorBody>;

  constructor(params: {
    status: number;
    detail: string;
    errorCode: string;
    details?: ApiErrorDetails;
    originalError?: AxiosError<ApiErrorBody>;
  }) {
    super(params.detail);
    this.name = 'ApiError';
    this.status = params.status;
    this.errorCode = params.errorCode;
    this.details = params.details ?? {};
    this.originalError = params.originalError;
  }

  /** For 422 validation errors, returns the list of field errors. */
  validationErrors(): ApiValidationError[] {
    if (this.status !== 422) return [];
    return this.details.errors ?? [];
  }

  isValidationError(): boolean {
    return this.status === 422;
  }

  isUnauthorized(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }

  isConflict(): boolean {
    return this.status === 409;
  }

  isServerError(): boolean {
    return this.status >= 500;
  }
}

/** Type guard para identificar ApiError. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Extract a human-readable error message from an ApiError or generic Error.
 * Used by UI components to display errors.
 */
export function getErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    if (err.isValidationError()) {
      const errors = err.validationErrors();
      if (errors.length > 0) {
        return errors.map((e) => `${e.field}: ${e.message}`).join('. ');
      }
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Ocurrió un error inesperado.';
}
